/**
 * A participant's card (Figma 1128:18877; the app's `RemotePlayerActionsPopup`), opened from their name pill: it stands at
 * the stage's bottom right without dimming it, and changes roles and access through the Share dialog's `ShareSource`.
 */
import { memo, useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from 'react';
import type { FollowController } from '../../collaboration/follow';
import type { PresenceSource } from '../../collaboration/presenceSource';
import type { ShareRole, ShareSource, ShareState } from '../../collaboration/share/source';
import { projectChat } from '../../collaboration/chat/controller';
import { agentSession } from '../../agent/runtime';
import { store } from '../../state/store';
import { t, type TranslationKey } from '../../i18n/I18n';
import { ActionMenu, type MenuItem } from '../Dropdown';
import { Alert } from '../Alert';
import { Icon } from '../Icon';
import { usePresence } from '../usePresence';
import { lookOf, ParticipantAvatar } from './CollaborationOverlay';
import { messageInChat } from './ChatPanel';
import { followBlocker, participantActions, type ParticipantAction } from './participantActions';
import { viewStatus } from './roster';

const ROLE: Record<ShareRole, TranslationKey> = { owner: 'Owner', editor: '#CanEdit', viewer: '#CanView' };
const ACTION: Record<ParticipantAction, TranslationKey> = {
  follow: '#WebParticipantFollow', unfollow: '#CollabStopFollowing', promote: '#PromoteToEditor', demote: '#DemoteToGuest', remove: '#RemoveAccess',
};
const noShare = () => () => {};
const notify = (text: string) => store.set({ notice: { text, key: (store.get().notice?.key ?? 0) + 1 } });

export type OpenParticipant = { key: string; opener: HTMLElement };

/** Keeps the card for its exit; another participant's pill replaces it at once. `onClose` names the card that asks. */
export const ParticipantPopup = memo(function ParticipantPopup({ target, presence, follow, share, onClose }: {
  target: OpenParticipant | null; presence: PresenceSource; follow: FollowController; share: ShareSource | null; onClose: (key: string) => void;
}) {
  const last = useRef(target);
  if (target) last.current = target;
  const { mounted, closing } = usePresence(!!target, 140);
  const shown = target ?? last.current;
  return mounted && shown ? <ParticipantCard key={shown.key} target={shown} closing={closing} presence={presence} follow={follow} share={share} onClose={onClose} /> : null;
});

function ParticipantCard({ target, closing, presence, follow, share, onClose }: {
  target: OpenParticipant; closing: boolean; presence: PresenceSource; follow: FollowController; share: ShareSource | null; onClose: (key: string) => void;
}) {
  const find = () => presence.get().participants.find(person => person.userId + ':' + person.clientId === target.key) ?? null;
  useSyncExternalStore(presence.subscribe, () => {
    const person = find(), look = person && lookOf(presence, person.userId);
    return person ? [person.displayName, look?.activity ?? '', look?.role, person.presence?.view.kind ?? '', !!person.viewport, presence.get().status,
      !!presence.get().pendingCommandId].join('\u0000') : '';
  });
  const shareState: ShareState | null = useSyncExternalStore(share?.subscribe ?? noShare, () => share?.get() ?? null);
  const following = useSyncExternalStore(follow.subscribe, follow.getSnapshot);
  const chatReady = useSyncExternalStore(projectChat.subscribe, () => !!projectChat.get().session);
  const busy = useSyncExternalStore(store.subscribe, () => store.documentBusy || store.get().isDragging);
  const editsAll = useSyncExternalStore(store.subscribe, () => store.get().edit === 'all');
  const card = useRef<HTMLElement>(null);
  const [confirm, setConfirm] = useState<'idle' | 'asking' | 'removing'>('idle');
  const person = find();

  const close = (restore: boolean) => {
    onClose(target.key);
    if (restore && target.opener.isConnected) target.opener.focus({ preventScroll: true });
  };
  const latestClose = useRef(close); latestClose.current = close;

  useEffect(() => { if (!person && !closing) onClose(target.key); }, [person, closing, onClose, target.key]);
  useEffect(() => { void share?.load(); }, [share]);
  useEffect(() => { if (target.opener.matches(':focus-visible')) card.current?.focus({ preventScroll: true }); }, [target.opener]);
  useEffect(() => {
    if (closing) return;
    // Before the editor's own Escape, which would let go of whoever you follow: the card closes first.
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || document.querySelector('dialog[open], .dropdown-panel[data-dropdown-open="true"], [aria-haspopup][aria-expanded="true"]')) return;
      event.preventDefault(); event.stopPropagation();
      latestClose.current(true);
    };
    const press = (event: PointerEvent) => {
      const node = event.target instanceof Element ? event.target : null;
      if (!node || card.current?.contains(node) || node.closest('.collab-cursor-pill.is-clickable, .collab-pose-name.is-clickable, dialog[open]')) return;
      latestClose.current(false);
    };
    window.addEventListener('keydown', key, true);
    document.addEventListener('pointerdown', press, true);
    return () => { window.removeEventListener('keydown', key, true); document.removeEventListener('pointerdown', press, true); };
  }, [closing]);

  if (!person) return null;
  const look = lookOf(presence, person.userId);
  const member = shareState?.status === 'ready' ? shareState.people.find(candidate => candidate.id === person.userId) ?? null : null;
  const role = member?.role ?? look.role;
  const view = viewStatus(person);
  const line = [look.activity, view && t(view)].filter(Boolean).join(' · '), activity = line.slice(0, 1).toLocaleUpperCase() + line.slice(1);
  const isFollowed = following.target?.userId === person.userId && following.target.clientId === person.clientId;
  const blocker = followBlocker({ busy: busy || !!presence.get().pendingCommandId, connected: presence.get().status === 'ready', viewport: !!person.viewport });
  const actions = participantActions({ agent: look.agent, following: isFollowed, myRole: shareState?.status === 'ready' ? shareState.myRole : null, person: member });

  const changeRole = (next: 'editor' | 'viewer') => {
    if (!share || !member) return;
    const name = person.displayName;
    void share.setRole(member.id, next).then(() => share.refresh())
      .then(() => notify(t(next === 'editor' ? '#UserNamePromotedToEditor' : '#UserNameChangedToGuest', name)), () => notify(t('#SharingUpdateFailed')));
  };
  const remove = async () => {
    if (!share || !member || confirm === 'removing') return;
    setConfirm('removing');
    try {
      await share.remove(member.id);
      await share.refresh();
      setConfirm('idle');
      close(true);
    } catch {
      setConfirm('idle');
      notify(t('#SharingUpdateFailed'));
    }
  };
  const run: Record<ParticipantAction, () => void> = {
    follow: () => { if (follow.start(person)) close(false); },
    unfollow: () => { follow.stop(); close(false); },
    promote: () => { changeRole('editor'); close(true); },
    demote: () => { changeRole('viewer'); close(true); },
    remove: () => setConfirm('asking'),
  };
  const items: MenuItem[] = actions.flatMap((action): MenuItem[] => [
    ...(action === 'remove' ? [{ id: 'separator', separator: true } as MenuItem] : []),
    { id: action, label: action === 'follow' && blocker ? `${t(ACTION.follow)} · ${t(blocker)}` : t(ACTION[action]),
      disabled: action === 'follow' && !!blocker, danger: action === 'remove', onSelect: run[action] },
  ]);

  return <>
    <section ref={card} className={'participant-popup' + (closing ? ' is-closing' : '')} role="dialog" aria-label={person.displayName} tabIndex={-1} inert={closing}
      style={{ '--participant-color': look.color } as CSSProperties} onPointerDown={event => event.stopPropagation()}>
      <header className="participant-popup-head">
        <ParticipantAvatar name={person.displayName} look={look} size={40} />
        <div className="participant-popup-text">
          <p className="participant-popup-name"><bdi>{person.displayName}</bdi><span> · {t(ROLE[role])}</span></p>
          {activity && <p className="participant-popup-activity">{activity}</p>}
        </div>
        <button type="button" className="participant-popup-close" aria-label={t('Close')} data-tip={t('Close')} onClick={() => close(true)}><Icon name="closeX" size={24} /></button>
      </header>
      <footer className="participant-popup-actions">
        {look.agent
          ? editsAll && <button type="button" className="participant-popup-message" onClick={() => { agentSession().setOpen(true); close(false); }}>
            <Icon name="aiStar" size={18} /><span>{t('#WebParticipantOpenChat')}</span></button>
          : chatReady && <button type="button" className="participant-popup-message" data-tip={t('#WebParticipantMessageTip')}
            onClick={() => { messageInChat(person.displayName); close(false); }}><Icon name="chat" size={18} /><span>{t('#WebParticipantMessage', person.displayName)}</span></button>}
        <ActionMenu label={t('#WebParticipantActions', person.displayName)} tip={t('#ShowMore')} tipSide="above" align="end" width={220}
          trigger={<Icon name="more" size={20} />} triggerClassName="participant-popup-more" items={items} />
      </footer>
    </section>
    {confirm !== 'idle' && member && <Alert title={t('#WebParticipantRemoveTitle', person.displayName)} text={t('#WebParticipantRemoveText')}
      secondary={{ label: t('Cancel'), onSelect: () => setConfirm('idle') }}
      primary={{ label: t('#RemoveAccess'), busy: confirm === 'removing', onSelect: () => void remove() }}
      onClose={() => { if (confirm !== 'removing') setConfirm('idle'); }} />}
  </>;
}
